import { Link } from 'react-router-dom';
import type { Post } from '@/mocks/posts';
import { formatViews, getCategoryBySlug } from '@/utils/site';

interface PostCardProps {
  post: Post;
  layout?: 'grid' | 'wide' | 'list';
}

export default function PostCard({ post, layout = 'grid' }: PostCardProps) {
  const category = getCategoryBySlug(post.categorySlug);
  const detailPath = `/detail/${post.id}`;

  if (layout === 'list') {
    return (
      <article className="group">
        <Link to={detailPath} className="flex gap-3 md:gap-4 items-start">
          <div className="relative w-24 h-[68px] md:w-32 md:h-[84px] shrink-0 overflow-hidden rounded-md bg-background-200">
            <img
              src={post.cover}
              alt={post.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="min-w-0">
            <span className="text-xs text-primary-600 font-medium">{category?.name}</span>
            <h3 className="mt-1 font-heading text-sm md:text-base text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
              {post.title}
            </h3>
            <div className="mt-2 flex items-center gap-3 text-[11px] text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {formatViews(post.views)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {post.readTime}
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (layout === 'wide') {
    return (
      <article className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors">
        <Link to={detailPath} className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative w-full h-52 md:h-full min-h-[220px] overflow-hidden bg-background-200">
            <img
              src={post.cover}
              alt={post.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <span className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
              <i className="ri-fire-line" />
              {category?.name}
            </span>
          </div>
          <div className="p-5 md:p-7 flex flex-col justify-center">
            <h3 className="font-heading text-xl md:text-2xl text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
              {post.title}
            </h3>
            <p className="mt-3 text-sm text-foreground-600 leading-7 line-clamp-3">{post.excerpt}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(post.views)} 次围观
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {post.date}
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex flex-col bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors">
      <Link to={detailPath} className="flex flex-col h-full">
        <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-200">
          <img
            src={post.cover}
            alt={post.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
            {category?.name}
          </span>
        </div>
        <div className="p-4 md:p-5 flex flex-col flex-1">
          <h3 className="font-heading text-base md:text-lg text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
            {post.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-6 line-clamp-2 flex-1">{post.excerpt}</p>
          <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-[11px] text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {formatViews(post.views)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {post.readTime}
            </span>
            <span className="text-primary-600 font-medium flex items-center gap-0.5">
              看详情
              <i className="ri-arrow-right-line" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}